import { round } from 'framework/base/utils/Utils';
import {
  isSameCurrencyPair,
  NEUTRAL_TRADE_RATE
} from 'framework/desktop/triplex-component/amountConsistency';

export function calculateFinalAmount(
  tradeAmount,
  tradeRate,
  calcRate,
  isDivision,
  rounding = 0,
  initialCalculation = true
) {
  if (isDivision && tradeRate && initialCalculation) {
    const amount = (tradeAmount * 10 ** calcRate) / tradeRate;

    if (tradeRate === 1) return amount;

    const roundedAmount = round(amount, -rounding);

    return roundedAmount;
  }
  if (!isDivision && calcRate !== undefined && initialCalculation) {
    const amount = (tradeAmount * tradeRate) / 10 ** calcRate;

    if (tradeRate === 1) return amount;

    const roundedAmount = round(amount, -rounding);

    return roundedAmount;
  }
  return null;
}

export function calculateTradeAmount(
  tradeRate,
  finalAmount,
  calcRate,
  isDivision,
  rounding = 0
) {
  if (isDivision && calcRate !== undefined) {
    const amount = (finalAmount * tradeRate) / 10 ** calcRate;

    if (tradeRate === 1) return amount;

    const roundedAmount = round(amount, -rounding);

    return roundedAmount;
  }
  if (!isDivision && tradeRate) {
    const amount = (finalAmount * 10 ** calcRate) / tradeRate;

    if (tradeRate === 1) return amount;

    const roundedAmount = round(amount, -rounding);

    return roundedAmount;
  }
  return null;
}

export function calculateTradeRate(
  initialAmountValue,
  equivalentAmountValue,
  calculationRate,
  isDivision
) {
  if (isDivision && equivalentAmountValue) {
    return (initialAmountValue * 10 ** calculationRate) / equivalentAmountValue;
  }
  if (!isDivision && initialAmountValue) {
    return (equivalentAmountValue * 10 ** calculationRate) / initialAmountValue;
  }
  return null;
}

export function calculateTradeRateBaseOnInitialAndEquivalent({
  initialCurrencyCode,
  initialAmountValue,
  equivalentCurrencyCode,
  equivalentAmountValue,
  setValues,
  calculationRate,
  isDivision,
  initialAmountDisabled
}) {
  if (isSameCurrencyPair(initialCurrencyCode, equivalentCurrencyCode)) {
    if (!initialAmountDisabled) {
      setValues.equivalentAmountValueOnChange(initialAmountValue);
    }
    setValues.tradeRateOnChange(NEUTRAL_TRADE_RATE);
  } else if (initialAmountValue && equivalentAmountValue) {
    const tradeRate = calculateTradeRate(
      initialAmountValue,
      equivalentAmountValue,
      calculationRate,
      isDivision
    );
    setValues.tradeRateOnChange(tradeRate);
  }
}

export function calculateInitialBaseOnEquivalentAndTradeRate({
  initialCurrencyCode,
  equivalentCurrencyCode,
  equivalentAmountValue,
  tradeRate,
  setValues,
  calculationRate,
  isDivision,
  initialCurrencyDecimalPlaces
}) {
  if (isSameCurrencyPair(initialCurrencyCode, equivalentCurrencyCode)) {
    setValues.tradeRateOnChange(NEUTRAL_TRADE_RATE);
    setValues.initialAmountValueOnChange(equivalentAmountValue || '');
  } else if (tradeRate > 0) {
    const resultAmount = calculateTradeAmount(
      tradeRate,
      equivalentAmountValue,
      calculationRate,
      isDivision,
      initialCurrencyDecimalPlaces
    );
    setValues.initialAmountValueOnChange(resultAmount);
  }
}

export function calculateEquivalentBaseOnInitialAndTradeRate({
  initialCurrencyCode,
  equivalentCurrencyCode,
  initialAmountValue,
  tradeRate,
  setValues,
  calculationRate,
  isDivision,
  equivalentCurrencyDecimalPlaces
}) {
  if (isSameCurrencyPair(initialCurrencyCode, equivalentCurrencyCode)) {
    setValues.tradeRateOnChange(NEUTRAL_TRADE_RATE);
    // Unconditional on purpose: this used to be guarded by
    // `!initialAmountDisabled` and then repeated without the guard one line
    // later, which made the guard dead. The mirror is kept (it is the effective
    // behaviour today, and the right one - a disabled initial amount is the
    // authoritative side, so the equivalent one has to follow it); only the
    // duplicate call is gone.
    setValues.equivalentAmountValueOnChange(initialAmountValue || '');
  } else {
    const resultAmount = calculateFinalAmount(
      initialAmountValue,
      tradeRate,
      calculationRate,
      isDivision,
      equivalentCurrencyDecimalPlaces
      // initialCalculation
    );
    setValues.equivalentAmountValueOnChange(resultAmount);
  }
}

export function calculateProfitRate({
  initialAmountValue,
  tradeRate,
  calculationRate,
  isDivision,
  equivalentCurrencyDecimalPlaces
}) {
  const resultAmount = calculateFinalAmount(
    initialAmountValue,
    tradeRate,
    calculationRate,
    isDivision,
    equivalentCurrencyDecimalPlaces
    // initialCalculation
  );
  return resultAmount;
}

export const isTriplexValueUnchanged = (currentVal, initialVal) => {
  if (currentVal === initialVal) return true;
  if (
    (currentVal === '' || currentVal === null || currentVal === undefined) &&
    (initialVal === '' || initialVal === null || initialVal === undefined)
  ) {
    return true;
  }
  const numCurrent = Number(currentVal);
  const numInitial = Number(initialVal);
  if (!Number.isNaN(numCurrent) && !Number.isNaN(numInitial)) {
    return numCurrent === numInitial;
  }
  return String(currentVal ?? '').trim() === String(initialVal ?? '').trim();
};

export const areTriplexValuesUnchanged = (currentFinancial, initialFinancial) => {
  if (!currentFinancial || !initialFinancial) return false;

  return (
    isTriplexValueUnchanged(currentFinancial.draftAmount, initialFinancial.draftAmount) &&
    isTriplexValueUnchanged(currentFinancial.draftCurrencyCode, initialFinancial.draftCurrencyCode) &&
    isTriplexValueUnchanged(currentFinancial.receivedAmount, initialFinancial.receivedAmount) &&
    isTriplexValueUnchanged(currentFinancial.receivedCurrencyCode, initialFinancial.receivedCurrencyCode) &&
    isTriplexValueUnchanged(currentFinancial.tradeRate, initialFinancial.tradeRate)
  );
};
